/**
 * cleanupDigest — in-memory audit trail of the current session's
 * housekeeping actions. Rendered in Settings → Cleanup Digest for
 * admins so they can see, at a glance, everything CDI just tidied up.
 *
 * Memory-only by design (per spec: "No backend storage / no API calls
 * / no schema changes"). The digest resets when the browser reloads.
 * If a session-long audit isn't enough, a future release can persist
 * to localStorage behind the same public API without touching any
 * caller.
 *
 * Public API
 * ----------
 *   addCleanupEvent(evt)   — record an event (see event shape below)
 *   getCleanupDigest()     — snapshot (newest-first) of the current list
 *   clearCleanupDigest()   — wipe the list
 *   useCleanupDigest()     — React hook that re-renders on every mutation
 *
 * Event shape
 * -----------
 *   {
 *     type:         "move" | "merge" | "delete" | "assign",
 *     itemName:     string,       // human-readable subject
 *     fromCategory: string|null,  // where it came from (or "Uncategorised")
 *     toCategory:   string|null,  // where it went (null for delete/merge)
 *     timestamp:    string        // ISO-8601, auto-filled if omitted
 *   }
 */
import { useEffect, useState } from "react";

// Module-level singleton — one digest per browser tab.
const _events = [];               // stored newest-first
const _subscribers = new Set();

const ALLOWED_TYPES = new Set(["move", "merge", "delete", "assign"]);

function _emit() {
  for (const fn of _subscribers) {
    try { fn(); } catch { /* subscriber errors never poison the emitter */ }
  }
}

/**
 * Push a new event onto the digest. Silently drops events with an
 * unknown `type` so future callers can't corrupt the log by typo.
 * `timestamp` auto-fills to the current ISO-8601 string if omitted.
 */
export function addCleanupEvent(event) {
  if (!event || typeof event !== "object") return;
  if (!ALLOWED_TYPES.has(event.type)) return;
  _events.unshift({
    type: event.type,
    itemName: event.itemName || "",
    fromCategory: event.fromCategory ?? null,
    toCategory: event.toCategory ?? null,
    timestamp: event.timestamp || new Date().toISOString(),
  });
  _emit();
}

/** Returns a fresh newest-first array so callers can't mutate our store. */
export function getCleanupDigest() {
  return _events.slice();
}

/** Wipe the digest — used by the "Clear Digest" button in Settings. */
export function clearCleanupDigest() {
  _events.length = 0;
  _emit();
}

/**
 * React hook — subscribes to the digest and returns the current
 * snapshot. Any component using this hook re-renders whenever
 * `addCleanupEvent` or `clearCleanupDigest` fires.
 */
export function useCleanupDigest() {
  const [snap, setSnap] = useState(() => getCleanupDigest());
  useEffect(() => {
    const fn = () => setSnap(getCleanupDigest());
    _subscribers.add(fn);
    return () => { _subscribers.delete(fn); };
  }, []);
  return snap;
}
